import { useTranslation } from 'next-i18next'
// Dependencies.
import styled, { css } from 'styled-components'
import { useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { Section as SECTION } from '../Section'
import { H3 } from '../H3'
import { Select } from '../Inputs/Select'
import { Option, FilterParams } from '../../types'
import { CategoriesContext } from '../../context'
import { colors, forScreensGreaterThanMobileWidth } from '../../utils'

// Section.
const Section = styled(SECTION)`
  .container {
    box-sizing: border-box;
    ${forScreensGreaterThanMobileWidth(css`
      height: 45px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    `)}
  }

  .searchArea {
    box-sizing: border-box;
    width: 100%;
    ${forScreensGreaterThanMobileWidth(css`
      display: flex;
      width: auto;
    `)}

    & .selectComponent {
      box-sizing: border-box;
      width: 100%;
      margin-top: 10px;
      ${forScreensGreaterThanMobileWidth(css`
        margin-top: 0;
        margin-left: 10px;
      `)}
    }

    & .dropdown {
      height: 100%;
      max-width: 100%;
      width: 100%;
      ${forScreensGreaterThanMobileWidth(
        css`
          width: 129px;
        `,
      )}
    }

    & .dropdown.category {
      width: 100%;
      ${forScreensGreaterThanMobileWidth(
        css`
          width: 207px;
        `,
      )}
    }

    & .dropdown.paid {
      width: 100%;
      ${forScreensGreaterThanMobileWidth(
        css`
          width: 200px;
        `,
      )}
    }
  }

  .dropdown + .dropdown {
    margin-left: 10px;
  }
`

// Props.
interface Props {
  category?: string
  sort?: string
  title?: string
  paid?: string
  className?: string
}

// Hero.
export const FunctionBar: React.FC<Props> = (props) => {
  const { t } = useTranslation('common')
  // Props
  const { category, sort, paid, title, className } = props

  // Global State
  const { categories, fetchCategories } = useContext(CategoriesContext)
  const router = useRouter()

  // Local State
  const [categoryValue, setCategoryValue] = useState<Option>(null)
  const [sortValue, setSortValue] = useState<Option>(null)
  const [paidValue, setPaidValue] = useState<Option>(null)
  const [routerParams, setRouterParams] = useState<FilterParams>({})

  // On any filter change
  const onFilterChange = (
    option: Option,
    filter: 'category' | 'sort' | 'paid',
  ) => {
    // Get Vales
    let newValue = option.value
    const newParams = { ...routerParams }

    // When the Value is 'Reset/all' then remove the value so the field resets
    if (newValue === 'Reset/all') {
      newValue = null
    }

    // Add new value to existing object
    newParams[filter as string] = newValue
    setRouterParams(newParams)

    // Update URI filters to drive new filter state
    const catParam =
      newParams.category !== null ? `category=${newParams.category || ''}&` : ''
    const paidParam =
      newParams.paid !== null ? `paid=${newParams.paid || ''}&` : ''
    const sortParam =
      newParams.sort !== null ? `sort=${newParams.sort || ''}&` : ''

    // Build Up string and remove trailing &
    let rootString = `/all-events?${catParam}${paidParam}${sortParam}`
    rootString = rootString.replace(/&\s*$/, '')
    router.push(rootString)
  }

  // On filter change update the value inside the filter
  useEffect(() => {
    // Set category
    let categoryOption = null
    if (category) {
      categoryOption = categoryOptions.find((c) => c.value === category)
    }
    setCategoryValue(categoryOption)

    // Set sort
    let sortOption = null
    if (sort) {
      sortOption = sortbyOptions.find((s) => s.value === sort)
    }
    setSortValue(sortOption)

    // Set paid
    let paidOption = null
    if (paid) {
      paidOption = paidTypeOptions.filter((p) => p.value === paid)
    }
    setPaidValue(paidOption)
  }, [category, sort, paid, categories])

  // Options.
  const categoryOptions = categories.map((category) => {
    const label = `${category.emoji || ''} ${category.title || ''}`
    const value = category.title
    return { label, value }
  })

  const sortbyOptions = [
    { label: 'A-Z', value: 'title-asc' },
    { label: 'Reset/all', value: 'Reset/all' },
  ]

  const paidTypeOptions = [
    { label: 'Reset/all', value: 'Reset/all' },
    { label: 'Free', value: '0' },
    { label: 'Paid', value: '1' },
  ]

  // ..
  return (
    <Section className={`FunctionBar ${className || ''}`}>
      <div className="container">
        {/* Header - Was Featured online activities*/}
        <H3>{title}</H3>

        {/* Search Bar*/}
        <div className="searchArea">
          <Select
            className="dropdown category"
            instanceId="categorySearchField"
            placeholder={t(`Filter by Category`, `Filter by Category`)}
            options={categoryOptions}
            onChange={(option: Option) => {
              onFilterChange(option, 'category')
            }}
            value={categoryValue}
          />

          <Select
            className="dropdown paid"
            instanceId="PaidTypeByField"
            placeholder={t(`Filter by free/paid`, `Filter by free/paid`)}
            options={paidTypeOptions}
            onChange={(option: Option) => {
              onFilterChange(option, 'paid')
            }}
            value={paidValue}
          />
          {/* value={"0"} */}

          <Select
            className="dropdown"
            instanceId="sortByField"
            placeholder={t(`Sort by`, `Sort by`)}
            options={sortbyOptions}
            onChange={(option: Option) => {
              onFilterChange(option, 'sort')
            }}
            value={sortValue}
          />
        </div>
      </div>
    </Section>
  )
}
